import { useInView } from "framer-motion";
import { useRef } from "react";
import { CellProps, Column } from "react-table";

import { SyncListItemFragment } from "@/apollo/types";
import { Badge } from "@/components/elements/Badge";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import { FormatTime } from "@/components/elements/FormatTime";
import Tooltip from "@/components/elements/Tooltip";
import { formatRowCount } from "@/shared/formatters";

import { ConnectorCell } from "./cells/ConnectorCell";
import { NextSyncCell } from "./cells/NextSyncCell";
import { ModelSourceCell, RawViewSourceCell } from "./cells/SourceCell";
import { SyncMenuCell } from "./cells/SyncMenuCell";
import { SyncStatusCell } from "./cells/SyncStatusCell";
import { ConnectorFilter } from "./filters/ConnectorFilter";
import { ErrorsFilter } from "./filters/ErrorsFilter";
import {
  ObjectTypeFilter,
  generateObjectIdentifier,
} from "./filters/ObjectTypeFilter";
import { SourceFilter } from "./filters/SourceFilter";
import { StatusFilter } from "./filters/StatusFilter";
import { connectorColumnSortFn } from "./sorting/connector-sort";
import { cronExprSortFn } from "./sorting/cron-expression-sort";
import { timeSortFn } from "./sorting/time-sort";

export type RowData = SyncListItemFragment & {
  modelPath?: string;
};

export const columns: Column<RowData>[] = [
  {
    id: "destination",
    accessor: (row) => row.destinationConnection.id,
    Header: "Destination",
    Cell: ({ row }: CellProps<RowData>) => {
      return <ConnectorCell rEltSync={row.original} />;
    },
    sortType: connectorColumnSortFn,
    maxWidth: 500,
    width: 500,
  },
  {
    id: "connector", // This is a hidden column used for filtering
    accessor: (row) => row.destinationConnection.id,
    Header: "Connector",
    Filter: ConnectorFilter,
  },
  {
    id: "primaryObject", // This is a hidden column used for filtering
    accessor: (row) => generateObjectIdentifier(row),
    Header: "Object",
    filter: "exact",
    Filter: ObjectTypeFilter,
  },
  {
    Header: "Source",
    Cell: ({ row }) => {
      if (row.original.rawView) {
        return <RawViewSourceCell rawView={row.original.rawView} />;
      }
      return <ModelSourceCell model={row.original.model} />;
    },
    accessor: "modelPath", // This accessor will be used for sorting.
    Filter: SourceFilter,
    maxWidth: 300,
    width: 300,
  },
  {
    Header: "Next sync",
    Cell: ({ row, value }: CellProps<RowData, RowData["syncInterval"]>) => {
      return <NextSyncCellWrapper row={row.original} syncInterval={value} />;
    },
    accessor: "syncInterval",
    sortType: cronExprSortFn,
    maxWidth: 200,
    width: 200,
  },
  {
    Header: "Last sync",
    id: "lastRun",
    accessor: (row) => row.job?.lastRun,
    Cell: ({
      row,
      value,
    }: CellProps<RowData, NonNullable<RowData["job"]>["lastRun"]>) => {
      return (
        <div className="text-muted-foreground">
          {typeof value === "number" ? (
            <Tooltip content={<FormatTime date={value} format="LLL" />}>
              <div className="truncate">
                <FormatRelativeTime date={value} />
              </div>
            </Tooltip>
          ) : (
            "-"
          )}
        </div>
      );
    },
    sortType: timeSortFn,
    maxWidth: 200,
    width: 200,
  },
  {
    Header: "Errors",
    accessor: "errorCount",
    Cell: ({ row, value }: CellProps<RowData, RowData["errorCount"]>) => {
      if (value === 0) {
        return <div className="text-muted-foreground">-</div>;
      }
      return (
        <Tooltip content={`${value} error(s) occurred`}>
          <Badge variant="danger" className="leading-normal">
            {formatRowCount(value)}
          </Badge>
        </Tooltip>
      );
    },
    filter: "errors",
    Filter: ErrorsFilter,
    maxWidth: 200,
    width: 200,
  },
  {
    Header: "Sync State",
    id: "state",
    Cell: ({ row }: CellProps<RowData, RowData["active"]>) => (
      <SyncStatusCell sync={row.original} />
    ),
    accessor: "active",
    disableSortBy: true,
    filter: "boolean",
    Filter: StatusFilter,
  },
  {
    Header: "",
    Cell: ({ row }) => (
      <div className="flex justify-end">
        <SyncMenuCell sync={row.original} />
      </div>
    ),
    width: 45,
    maxWidth: 45,
    accessor: "active",
    disableSortBy: true,
    filter: "exact",
  },
  {
    accessor: "createdAt",
  },
];

function NextSyncCellWrapper(props: {
  syncInterval?: string | null;
  row: RowData;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: false,
    amount: "some",
  });
  return <div ref={ref}>{isInView && <NextSyncCell {...props} />}</div>;
}
